/**
 * DJH · "Open until 6:00 PM" / "Closed · opens Saturday 10:30 AM"
 *
 * The question every visitor to a Visit page actually has, answered from data the
 * page is already carrying.
 *
 * ── IT READS THE JSON-LD, AND THAT IS THE WHOLE POINT ────────────────────────────
 *
 * The shop's hours already exist TWICE in this repo: hardcoded as
 * `openingHoursSpecification` in djh-structured-data.liquid, and typed as prose in
 * the visit_map section's `hours` setting. They agree today because someone typed
 * them correctly twice, which is not a property that survives an edit.
 *
 * So this badge adds NO THIRD COPY. It parses the openingHoursSpecification already
 * in the page — the machine-readable one, the one Google reads — and the prose
 * beside it becomes display only. `qa-hours-parity.py` guards that the two existing
 * copies keep agreeing; that check is the reason it is safe to treat the JSON-LD as
 * authoritative here.
 *
 * ── TIME IS THE SHOP'S, NEVER THE VISITOR'S ──────────────────────────────────────
 *
 * Computed in Pacific/Honolulu, explicitly, via Intl. A customer in California
 * asking "are they open?" means the SHOP, and their own clock is three hours wrong
 * about it. This is D-102's lesson — the ops side has shop_today() because we got
 * this wrong once already, with money.
 *
 * HAWAII DOES NOT OBSERVE DAYLIGHT SAVING. HST is UTC-10 all year, so there is no
 * spring-forward gap to handle. That is a fact about the timezone, not an assumption
 * being taken for granted — and it is written down here precisely because it is the
 * kind of thing someone later "fixes" into a bug by adding DST handling that then
 * mis-fires for half the year. The Intl call would be correct either way; this note
 * is so nobody replaces the Intl call with arithmetic.
 *
 * ── NO JAVASCRIPT, NO BADGE ──────────────────────────────────────────────────────
 *
 * The element ships `hidden` and only appears once a real status has been computed.
 * A status line that renders empty, or worse renders a stale guess, is worse than
 * the hours table it sits above — which is complete, correct and needs no script.
 */
(function () {
  // EVERY host, not the first one. The homepage now asks the same question twice —
  // once in the hero, where it is a reason to drive to Waipahu, and once in the footer
  // beside the address, where it has always been. `querySelector` filled whichever came
  // first in the document and left the other one `hidden` forever, so adding the hero
  // silently emptied the footer. Henry noticed the footer had gone quiet; nothing had
  // been moved, the hero was simply winning the race.
  var hosts = document.querySelectorAll('[data-djh-hours-status]');
  if (!hosts.length) return;
  if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) return;

  // The section that owns a badge loads this asset, so a page carrying two of them
  // requests the script twice and the browser runs it twice. Harmless — the writes are
  // idempotent — but the second pass would also arm a second timer, and the interval
  // is what keeps it right across a closing time. One run, one timer.
  if (document.documentElement.hasAttribute('data-djh-hours-armed')) return;
  document.documentElement.setAttribute('data-djh-hours-armed', '');

  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  /** Every openingHoursSpecification in the page's JSON-LD, wherever it is nested. */
  function readSpecs() {
    var out = [];
    var nodes = document.querySelectorAll('script[type="application/ld+json"]');
    for (var i = 0; i < nodes.length; i++) {
      var data;
      try {
        data = JSON.parse(nodes[i].textContent);
      } catch (e) {
        continue; // a malformed block is not this badge's problem to report
      }
      collect(data, out);
    }
    return out;
  }

  function collect(node, out) {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      for (var i = 0; i < node.length; i++) collect(node[i], out);
      return;
    }
    if (node.openingHoursSpecification) {
      var spec = node.openingHoursSpecification;
      var list = Array.isArray(spec) ? spec : [spec];
      for (var j = 0; j < list.length; j++) out.push(list[j]);
    }
    for (var k in node) {
      if (Object.prototype.hasOwnProperty.call(node, k) && k !== 'openingHoursSpecification') {
        collect(node[k], out);
      }
    }
  }

  function toMinutes(hhmm) {
    if (typeof hhmm !== 'string') return null;
    var m = hhmm.match(/^(\d{1,2}):(\d{2})/);
    if (!m) return null;
    return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  }

  /** day index -> [{open, close}], from the spec entries. */
  function buildWeek(specs) {
    var week = [[], [], [], [], [], [], []];
    for (var i = 0; i < specs.length; i++) {
      var s = specs[i];
      var open = toMinutes(s.opens);
      var close = toMinutes(s.closes);
      if (open === null || close === null || close <= open) continue;
      var days = Array.isArray(s.dayOfWeek) ? s.dayOfWeek : [s.dayOfWeek];
      for (var d = 0; d < days.length; d++) {
        var name = String(days[d] || '').replace(/^https?:\/\/schema\.org\//, '');
        var idx = DAYS.indexOf(name);
        if (idx >= 0) week[idx].push({ open: open, close: close });
      }
    }
    return week;
  }

  /** The shop's own weekday and minute-of-day, not the visitor's. */
  function shopNow() {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Pacific/Honolulu',
      weekday: 'long',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(new Date());

    var weekday = null;
    var hour = null;
    var minute = null;
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].type === 'weekday') weekday = parts[i].value;
      if (parts[i].type === 'hour') hour = parseInt(parts[i].value, 10);
      if (parts[i].type === 'minute') minute = parseInt(parts[i].value, 10);
    }
    var day = DAYS.indexOf(weekday);
    if (day < 0 || hour === null || minute === null) return null;
    // Intl can render midnight as 24 under hour12:false in some engines.
    if (hour === 24) hour = 0;
    return { day: day, minutes: hour * 60 + minute };
  }

  // "6:00 PM", not "6pm" and not "18:00" — Henry, 2026-08-30, and the same format the
  // hours list beside this uses. Minutes are always shown: "6 PM" and "6:00 PM" next to
  // each other in one block reads as two different kinds of fact.
  function label(minutes) {
    var h = Math.floor(minutes / 60);
    var m = minutes % 60;
    var suffix = h >= 12 ? 'PM' : 'AM';
    var h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return h12 + ':' + (m < 10 ? '0' + m : m) + ' ' + suffix;
  }

  function describe(week, now) {
    var today = week[now.day];
    for (var i = 0; i < today.length; i++) {
      if (now.minutes >= today[i].open && now.minutes < today[i].close) {
        return { open: true, text: 'Open until ' + label(today[i].close) };
      }
    }
    // Later today?
    var later = null;
    for (var j = 0; j < today.length; j++) {
      if (now.minutes < today[j].open && (later === null || today[j].open < later)) {
        later = today[j].open;
      }
    }
    if (later !== null) return { open: false, text: 'Closed · opens today at ' + label(later) };

    // The next day that has any hours at all.
    for (var ahead = 1; ahead <= 7; ahead++) {
      var idx = (now.day + ahead) % 7;
      var slots = week[idx];
      if (!slots.length) continue;
      var earliest = slots[0].open;
      for (var k = 1; k < slots.length; k++) if (slots[k].open < earliest) earliest = slots[k].open;
      var when = ahead === 1 ? 'tomorrow' : DAYS[idx];
      return { open: false, text: 'Closed · opens ' + when + ' at ' + label(earliest) };
    }
    return null; // no hours anywhere — say nothing rather than guess
  }

  function render() {
    var week = buildWeek(readSpecs());
    var any = false;
    for (var i = 0; i < week.length; i++) if (week[i].length) any = true;
    if (!any) return;

    var now = shopNow();
    if (!now) return;

    var status = describe(week, now);
    if (!status) return;

    for (var h = 0; h < hosts.length; h++) {
      var host = hosts[h];
      host.textContent = status.text;
      host.classList.toggle('is-open', status.open);
      host.classList.toggle('is-closed', !status.open);
      host.hidden = false;
    }
  }

  render();
  // A visitor can sit on this page across a closing time. Recomputing costs nothing
  // and there is no aria-live: this is ambient information, and interrupting a
  // screen reader mid-sentence to announce that a shop just shut is not a kindness.
  setInterval(render, 60000);
})();
